import React, { useState } from "react";
import { CheckCircle2, CreditCard, MessageCircle, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { WHATSAPP } from "../mock";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function CheckoutModal({ open, onOpenChange }) {
  const { t, S, lang } = useLang();
  const { items, total, clear, setIsOpen } = useCart();
  const [step, setStep] = useState("form"); // form | processing | success
  const [method, setMethod] = useState("card");
  const [data, setData] = useState({ name: "", email: "", phone: "" });

  const C = S.checkout;
  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const reset = () => {
    setStep("form");
    setData({ name: "", email: "", phone: "" });
    setMethod("card");
  };

  const handlePay = () => {
    if (method === "whatsapp") {
      const lines = [
        `*XPERIENCIAS MEXICO - ${lang === "es" ? "Pedido" : "Order"}*`,
        ...items.map((i) => `• ${t(i.name)} x${i.qty} = $${i.price * i.qty} USD`),
        `${t(C.name)}: ${data.name}`,
        `Total: $${total} USD`,
      ];
      window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
    }
    // Simulated payment (demo). Real gateway (e.g. Stripe) to be integrated later.
    setStep("processing");
    setTimeout(() => {
      setStep("success");
      clear();
    }, 1800);
  };

  const close = () => {
    onOpenChange(false);
    if (step === "success") {
      setIsOpen(false);
    }
    setTimeout(reset, 300);
  };

  const valid = data.name && data.phone && (method === "whatsapp" || data.email);

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        {step === "success" ? (
          <div className="text-center py-6">
            <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4">
              <CheckCircle2 className="w-9 h-9" />
            </span>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-center">{t(C.success)}</DialogTitle>
              <DialogDescription className="text-center mt-2">{t(C.successSub)}</DialogDescription>
            </DialogHeader>
            <Button onClick={close} className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8">
              {t(C.close)}
            </Button>
          </div>
        ) : step === "processing" ? (
          <div className="text-center py-12">
            <Loader2 className="mx-auto w-12 h-12 text-primary animate-spin" />
            <p className="mt-5 font-display font-semibold text-lg">{lang === "es" ? "Procesando pago..." : "Processing payment..."}</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{t(C.title)}</DialogTitle>
              <DialogDescription>{t(C.sub)}</DialogDescription>
            </DialogHeader>

            <div className="max-h-40 overflow-y-auto space-y-2 rounded-xl bg-secondary/40 p-3 my-1">
              {items.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground line-clamp-1 mr-2">{t(i.name)} <span className="text-muted-foreground">x{i.qty}</span></span>
                  <span className="font-semibold text-primary shrink-0">${i.price * i.qty}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t border-border pt-2 mt-1">
                <span className="font-semibold">{t(C.title === C.title ? S.cart.total : S.cart.total)}</span>
                <span className="font-display font-bold text-primary">${total} USD</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="mb-1.5 block text-sm">{t(C.name)} *</Label>
                <Input value={data.name} onChange={(e) => set("name", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-sm">{t(C.phone)} *</Label>
                  <Input value={data.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">{t(C.email)}</Label>
                  <Input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} />
                </div>
              </div>

              <div>
                <Label className="mb-1.5 block text-sm">{t(C.method)}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setMethod("card")}
                    className={`flex items-center gap-2 justify-center rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${method === "card" ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                  >
                    <CreditCard className="w-4 h-4" /> {t(C.card)}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMethod("whatsapp")}
                    className={`flex items-center gap-2 justify-center rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${method === "whatsapp" ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">{t(C.demoNote)}</p>

              <Button
                onClick={handlePay}
                disabled={!valid}
                size="lg"
                className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold rounded-full h-12 disabled:opacity-50"
              >
                {method === "whatsapp" ? t(C.payWhatsapp) : `${t(C.pay)} $${total} USD`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
